import { stripeClient } from "@better-auth/stripe/client";
import { oauthProviderClient } from "@better-auth/oauth-provider/client";
import { createAuthClient } from "better-auth/react";
import { adminClient, emailOTPClient, organizationClient } from "better-auth/client/plugins";
import { ac, roles, PLATFORM_SESSION_CHANGED } from "@lawline/shared/access/platform";
import type { createPageSession } from "@platform/core/page-session";

// The plugin list must mirror the platformAuth config in apps/api.

export type CreatePlatformAuthClientOptions = {
	baseURL: string; // Full base URL, for example https://api.lawline.se/platform/auth
	fetchOptions?: RequestInit;
	sessionBinding?: ReturnType<typeof createPageSession>;
	// False leaves a returned redirect to the caller, which checks it before it navigates.
	followRedirects?: boolean;
};

export function resolveAuthBaseUrl(configuredBase: string, origin: string | undefined): string {
	const base = configuredBase || origin;
	if (!base) throw new Error("Platform auth requires an absolute HTTP(S) origin");

	const url = new URL("/platform/auth", base);
	if (url.protocol !== "http:" && url.protocol !== "https:") {
		throw new Error("Platform auth requires an absolute HTTP(S) origin");
	}
	return url.toString();
}

export function createPlatformAuthClient(options: CreatePlatformAuthClientOptions) {
	return createAuthClient({
		baseURL: options.baseURL,
		disableDefaultFetchPlugins: options.followRedirects === false,
		fetchOptions: {
			credentials: "include",
			...options.fetchOptions,
			onRequest: (context) => {
				for (const [name, value] of Object.entries(options.sessionBinding?.headers() ?? {})) {
					if (!context.headers.has(name)) context.headers.set(name, value);
				}
			},
			onSuccess: (context) => {
				if (new URL(context.request.url).pathname.endsWith("/get-session")) {
					options.sessionBinding?.observe(context.data?.session ?? null);
				}
			},
			onError: (context) => {
				if (context.error.code === PLATFORM_SESSION_CHANGED) options.sessionBinding?.invalidate();
			},
		},
		plugins: [
			oauthProviderClient(),
			organizationClient({ ac, roles }),
			emailOTPClient(),
			adminClient(),
			stripeClient({ subscription: true }),
		],
	});
}

export { ac, roles, statement } from "@lawline/shared/access/platform";
